<template>
  <v-dialog :value="value" :max-width="maxWidth" persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        <span class="text-h6">{{ title }}</span>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="isFormValid" lazy-validation>
          <v-container>
            <v-row>
              <v-col :cols="12" :md="getColSize(field)" v-for="field in fields" :key="field.key">
                <!-- Text Field -->
                <v-text-field
                  v-if="field.type === 'text'"
                  v-model="formData[field.key]"
                  :label="field.label"
                  dense
                  :disabled="loading || field.disabled"
                  :rules="field.rules || []"
                  :error-messages="getFieldErrors(field.key)"
                  :maxlength="field.maxLength"
                  :counter="field.maxLength"
                  @keypress="handleKeypress($event, field)"
                  @input="handleInput(field.key, field)"
                  @change="clearFieldError(field.key)"
                ></v-text-field>

                <!-- Textarea -->
                <v-textarea
                  v-else-if="field.type === 'textarea'"
                  v-model="formData[field.key]"
                  :label="field.label"
                  dense
                  :rows="field.rows || 3"
                  :disabled="loading || field.disabled"
                  :rules="field.rules || []"
                  :error-messages="getFieldErrors(field.key)"
                  :maxlength="field.maxLength"
                  :counter="field.maxLength"
                  @change="clearFieldError(field.key)"
                ></v-textarea>

                <!-- Select -->
                <v-select
                  v-else-if="field.type === 'select'"
                  v-model="formData[field.key]"
                  :label="field.label"
                  :items="field.items"
                  dense
                  :disabled="loading || field.disabled"
                  :rules="field.rules || []"
                  :error-messages="getFieldErrors(field.key)"
                  @change="clearFieldError(field.key)"
                ></v-select>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" class="mb-2 rounded-pill elevation-0" text @click="cancel" :disabled="loading">
          CANCEL
        </v-btn>
        <v-btn color="primary" class="mb-2 rounded-pill elevation-0" @click="save" :loading="loading" :disabled="saveDisabled">
          SAVE
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'FormMaster',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Form',
    },
    fields: {
      type: Array,
      required: true,
    },
    editedItem: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: String,
      default: '500px',
    },
    cols: {
      type: Number,
      default: 1,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    requireDirty: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formData: {},
      isFormValid: false,
      fieldErrors: {},
    };
  },
  computed: {
    rulesPassed() {
      return this.fields.every((field) =>
        (field.rules || []).every((rule) => rule(this.formData[field.key]) === true)
      );
    },
    isDirty() {
      const normalize = (v) => (v === null || v === undefined ? '' : String(v));
      return this.fields.some(
        (field) => normalize(this.formData[field.key]) !== normalize(this.editedItem[field.key])
      );
    },
    saveDisabled() {
      if (this.loading) return true;
      if (!this.rulesPassed) return true;
      if (this.requireDirty && !this.isDirty) return true;
      return false;
    },
  },
  watch: {
    editedItem: {
      handler(val) {
        this.formData = { ...val };
      },
      immediate: true,
      deep: true,
    },
    value(val) {
      if (val) {
        this.formData = { ...this.editedItem };
        this.fieldErrors = {};
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.resetValidation();
          }
        });
      }
    },
    errors: {
      handler(val) {
        if (val && typeof val === 'object') {
          this.fieldErrors = { ...val };
        }
      },
      deep: true,
    },
  },
  methods: {
    getColSize(field) {
      if (field.cols) return field.cols;
      return this.cols === 2 ? 6 : 12;
    },
    handleKeypress(event, field) {
      if (field.inputType === 'number') {
        const charCode = event.which || event.keyCode;
        if (charCode < 48 || charCode > 57) {
          event.preventDefault();
        }
      }
    },
    handleInput(key, field) {
      if (field.uppercase && this.formData[key]) {
        this.formData[key] = this.formData[key].toUpperCase();
      }
      this.clearFieldError(key);
    },
    getFieldErrors(fieldKey) {
      return this.fieldErrors[fieldKey] || [];
    },
    clearFieldError(fieldKey) {
      if (this.fieldErrors[fieldKey]) {
        const newErrors = { ...this.fieldErrors };
        delete newErrors[fieldKey];
        this.fieldErrors = newErrors;
      }
    },
    cancel() {
      this.fieldErrors = {};
      this.$refs.form.resetValidation();
      this.$emit('cancel');
      this.$emit('input', false);
    },
    save() {
      if (this.$refs.form.validate()) {
        this.$emit('save', { ...this.formData });
      }
    },
  },
};
</script>